import AsyncStorage from '@react-native-async-storage/async-storage';
import { NativeModules, Platform } from 'react-native';
import { parseInstallReferrer } from './vibeClaimLinks';
import { savePendingVibeClaim } from './pendingVibeClaim';

// Android only: Google Play Install Referrer (TonyteInstallReferrerModule.kt).
// When the web game sends someone without Tonyte to the Play Store, the claim
// reference rides along in `referrer=` and comes back here on first launch —
// the only deferred deep link that survives an install without a third-party
// service. iOS has no equivalent; it relies on the explicit recovery paths
// (tap "Find my vibes" again, recovery code, verified email).

const CHECKED_KEY = 'tonyte.vibeClaim.referrerChecked.v1';
const MAX_ATTEMPTS = 3;
// Referrer data lives for 90 days and is per *install*, so an app update can
// still return the referrer from the original install. Only a recent install
// can belong to a game that is still claimable (rounds end at midnight).
const MAX_INSTALL_AGE_MS = 48 * 60 * 60 * 1000;

type NativeReferrer = { referrer: string; installBeginTimestampSeconds: number } | null;

export async function checkInstallReferrerOnce(now = Date.now()): Promise<void> {
  if (Platform.OS !== 'android') return;
  const native = NativeModules.TonyteInstallReferrer;
  if (!native?.getInstallReferrer) return;

  let attempts = 0;
  try {
    const raw = await AsyncStorage.getItem(CHECKED_KEY);
    if (raw === 'done') return;
    attempts = Number(raw) || 0;
    if (attempts >= MAX_ATTEMPTS) return;
  } catch {
    return;
  }

  let details: NativeReferrer;
  try {
    details = await native.getInstallReferrer();
  } catch {
    details = null;
  }
  if (details === null) {
    // Play service unavailable right now — try again on a later launch.
    await AsyncStorage.setItem(CHECKED_KEY, String(attempts + 1)).catch(() => {});
    return;
  }
  await AsyncStorage.setItem(CHECKED_KEY, 'done').catch(() => {});

  const installedAtMs = (details.installBeginTimestampSeconds || 0) * 1000;
  if (installedAtMs && now - installedAtMs > MAX_INSTALL_AGE_MS) return;
  const credential = parseInstallReferrer(details.referrer);
  if (credential) await savePendingVibeClaim(credential, 'install_referrer');
}
